import { TIPO_ARIA } from "../constants";
import { puedeVerLogistica } from "./permisos";
import {
  CAMPOS_LOGISTICA,
  CAMPO_FECHA,
  CAMPO_MATERIA,
  NOMBRES_LOGISTICA,
} from "./campos";

const OBLIGATORIO = "Este campo es obligatorio";

/*
 * Una validacion por paso del formulario (ver model/pasos.js). Todas reciben
 * las mismas partes y devuelven la misma forma:
 *   errores  -> { campo: mensaje }, para pintar junto a cada campo.
 *   mensaje  -> aviso general, para lo que no es un campo (las listas).
 */

const soloAviso = (mensaje) => ({ errores: {}, mensaje });

/** Clase: el backend exige la materia siempre que la solicitud lleva reserva. */
export const validarClase = ({ valores }) =>
  valores[CAMPO_MATERIA.name]
    ? soloAviso("")
    : { errores: { [CAMPO_MATERIA.name]: OBLIGATORIO }, mensaje: "" };

/** Reserva: los campos de logistica y el orden de las horas. */
export const validarReserva = ({ valores }) => {
  const errores = {};

  CAMPOS_LOGISTICA.forEach(({ name, minimoCaracteres, mensajeInvalido }) => {
    const valor = valores[name];

    if (!valor) {
      errores[name] = OBLIGATORIO;
      return;
    }
    if (minimoCaracteres && `${valor}`.length < minimoCaracteres) {
      errores[name] = mensajeInvalido;
    }
  });

  const { hora_entrega: inicio, hora_retiro: fin } = valores;
  if (inicio && fin && inicio >= fin) {
    errores.hora_retiro =
      "La hora de inicio es superior o igual a la hora de fin";
  }

  return { errores, mensaje: "" };
};

/** Estudiantes: cuentan solo los marcados, que son los que se envian. */
export const validarEstudiantes = ({ estudiantes }) =>
  soloAviso(estudiantes.length ? "" : "Debe seleccionar por lo menos un estudiante.");

/** Libros en Libro a tu clase, niveles en ARIA. */
export const validarMaterial = ({ tipo, libros, capacitaciones, nivelesBloqueados = [] }) => {
  if (tipo === TIPO_ARIA) {
    if (!capacitaciones.length) return soloAviso("Ingrese por lo menos un nivel.");
    // Mas de la mitad del grupo ya los tiene este periodo (como en AGIL).
    return soloAviso(
      nivelesBloqueados.length
        ? `Quite ${nivelesBloqueados.join(", ")}: más de la mitad de los estudiantes ya lo tiene este periodo.`
        : "",
    );
  }
  return soloAviso(libros.length ? "" : "Ingrese por lo menos un libro o una tematica.");
};

export const hayErrores = (errores) => Object.keys(errores).length > 0;

/** Un paso esta completo si no marca ningun campo ni deja aviso. */
export const esValido = ({ errores, mensaje }) => !hayErrores(errores) && !mensaje;

// Materia elegida del catalogo de SICUC, en los campos que espera el backend.
const datosMateria = (materia) => ({
  cod_materia: materia?.cod_materia ?? "",
  materia: materia?.materia ?? "",
  grupo: materia?.grupo ?? "",
  programa: materia?.nombre_programa ?? "",
});

/**
 * Body de POST api/v1.0/biblioteca/solicitudes.
 *
 * Los campos que no aplican al perfil van en "": el backend los trata como
 * ausentes. No se envia el periodo ni el solicitante: los pone el servidor,
 * que registra siempre a quien esta en sesion.
 */
export const construirPayload = ({
  perfil,
  tipo,
  valores,
  materia,
  estudiantes,
  libros,
  capacitaciones,
}) => {
  const conLogistica = puedeVerLogistica(perfil, tipo);
  const esAria = tipo === TIPO_ARIA;

  // Sin logistica solo viaja la fecha: la que propone el estudiante en ARIA.
  const viaja = (name) => conLogistica || name === CAMPO_FECHA.name;

  const logistica = NOMBRES_LOGISTICA.reduce(
    (acumulado, name) => ({
      ...acumulado,
      [name]: viaja(name) ? valores[name] : "",
    }),
    {},
  );

  return {
    id_tipo_solicitud: tipo,
    ...datosMateria(conLogistica ? materia : null),
    ...logistica,
    estudiantes: estudiantes.map(({ identificacion, nombre }) => ({
      identificacion,
      nombre,
    })),
    libros: esAria ? [] : libros.map(({ nombre }) => nombre),
    capacitaciones: esAria
      ? capacitaciones.map(({ id_capacitacion }) => ({ id_capacitacion }))
      : [],
  };
};

/**
 * Estudiantes con los que abre el formulario: los guardados, si se esta
 * editando. Si quien pide es estudiante, el mismo va de primero y `fijo`:
 * queda inscrito en su propia solicitud y no se puede quitar.
 *
 * Recibe datos simples y no el usuario completo, para que quien la use
 * pueda depender de ellos sin rehacer la lista cada vez que cambie el
 * objeto del usuario.
 */
export const estudiantesIniciales = ({
  guardados,
  identificacion,
  nombre,
  inscribirSolicitante,
}) => {
  const lista = guardados ?? [];
  if (!inscribirSolicitante || !identificacion) return lista;

  const propia = `${identificacion}`;
  const otros = lista.filter((estudiante) => `${estudiante.identificacion}` !== propia);

  return [
    {
      identificacion: propia,
      nombre,
      fijo: true,
      detalle: "Eres quien solicita: quedas inscrito",
    },
    ...otros,
  ];
};

export const nombreCompleto = (usuario) =>
  [
    usuario?.primer_nombre,
    usuario?.segundo_nombre,
    usuario?.primer_apellido,
    usuario?.segundo_apellido,
  ]
    .filter(Boolean)
    .join(" ");
